import { useState } from "react";
import { ChevronDown, Heart, Loader2 } from "lucide-react";
import { submitStory, formatApiError } from "../../lib/api";

const MAX = 280;

export const StoryBox = ({ email }) => {
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const len = quote.trim().length;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return setError("Please tick the box so we can share your story.");
    if (!len) return setError("Please share a few words first.");
    if (len > MAX) return setError("Please keep your story to 280 characters or fewer.");
    setError("");
    setLoading(true);
    try {
      await submitStory({ email, quote: quote.trim(), display_name: name.trim() || null, consent });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <p data-testid="story-thank-you" className="status-pill mt-4 bg-[#EAF0EC] text-[#2F4537]">
        <Heart className="h-5 w-5 shrink-0" /> Thank you for sharing — we'll read every word.
      </p>
    );

  return (
    <div data-testid="story-box" className="mt-4 rounded-[1.5rem] border border-[#FDFBF7]/15 bg-[#FDFBF7]/10 backdrop-blur-md">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="story-toggle-button"
        className="flex w-full items-center justify-between gap-3 p-5 sm:px-6 text-left">
        <span className="font-serif text-xl text-[#FDFBF7]">Why does balance matter to you? <span className="font-sans text-sm text-[#F4EBDD]/60">(optional)</span></span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#F2C98F] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form onSubmit={submit} noValidate className="px-5 pb-5 sm:px-6 sm:pb-6 fade-up">
          <label htmlFor="story-quote" className="sr-only">Your story</label>
          <textarea id="story-quote" rows={4} value={quote} onChange={(e) => { setQuote(e.target.value); setError(""); }}
            placeholder="I want to be at every school pick-up…" data-testid="story-quote-input"
            className="w-full resize-none rounded-2xl bg-[#FDFBF7] px-4 py-3 text-base text-[#2C221E] placeholder:text-[#8A7A72] outline-none" />
          <p data-testid="story-char-count" className={`mt-1 text-right text-xs ${len > MAX ? "text-[#FFB59A]" : "text-[#F4EBDD]/60"}`}>{len}/{MAX}</p>
          <label htmlFor="story-name" className="block text-sm font-medium text-[#FDFBF7]">Your name <span className="text-[#F4EBDD]/60">(optional, e.g. "Sarah, mum of two")</span></label>
          <input id="story-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-testid="story-name-input"
            className="mt-2 w-full rounded-full bg-[#FDFBF7] px-4 py-2.5 text-base text-[#2C221E] outline-none" />
          <label className="mt-4 flex items-start gap-3 text-sm text-[#F4EBDD]">
            <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setError(""); }} data-testid="story-consent-checkbox"
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#C85A32]" />
            Woven may share this on the website
          </label>
          {error && <p role="alert" data-testid="story-error-message" className="mt-3 text-sm font-medium text-[#FFB59A]">{error}</p>}
          <button type="submit" disabled={loading || !consent} data-testid="story-submit-button" className="btn-soft mt-4 bg-transparent text-[#FDFBF7] border-[#FDFBF7]/50 disabled:opacity-50 disabled:cursor-not-allowed">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Share my story
          </button>
        </form>
      )}
    </div>
  );
};
